import { useState, useEffect } from "react";
import { Modal, Button, TextField, Box } from "@mui/material";
import AuthService from "services/auth-service";
import Autocomplete from "@mui/material/Autocomplete";
import { useMaterialUIController, toggleReloadAccounts } from "context";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    borderRadius: 2,
    boxShadow: 24,
    p: 4,
};

const SaveModal = ({ open, handleClose, account }) => {
    const [, dispatch] = useMaterialUIController();
    const [types, setTypes] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    const [formData, setFormData] = useState({
        name: "",
        type: "",
        start_day: 1,
        currency: "",
        description: "",
    });

    useEffect(() => {
        fetchTypes();
        fetchCurrencies();
    }, []);

    useEffect(() => {
        setFormData({
            name: account ? account.name : "",
            type: account ? account.type : "",
            currency: account ? account.currency : "",
            start_day: account ? account.start_day : "",
            description: account ? account.description : "",
        });
    }, [account]);

    const fetchTypes = async () => {
        const response = await AuthService.getEnums("account_types");
        setTypes(
            Object.entries(response).map(([key, value]) => ({
                label: key,
                value: value,
            }))
        );
    };

    const fetchCurrencies = async () => {
        const response = await AuthService.getEnums("currencies");
        setCurrencies(
            Object.entries(response).map(([key, value]) => ({
                label: key,
                value: value,
            }))
        );
    };

    const handleTypeChange = (event, newValue) => {
        setFormData({ ...formData, type: newValue ? newValue.value : "" });
    };

    const handleCurrencyChange = (event, newValue) => {
        setFormData({ ...formData, currency: newValue ? newValue.value : "" });
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleSubmit = async () => {
        account ? await AuthService.updateAccount(account.id, formData) : await AuthService.createAccount(formData);
        toggleReloadAccounts(dispatch, Date.now());
        handleClose();
    };

    const handleDelete = async () => {
        await AuthService.deleteAccount(account.id);
        toggleReloadAccounts(dispatch, Date.now());
        handleClose();
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <h2>{account ? "Edit Account" : "Create Account"}</h2>
                <TextField
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    type="text"
                />
                
                <Autocomplete
                    options={types}
                    getOptionLabel={(option) => option.label}
                    value={types.find((option) => option.value === formData.type) || null}
                    onChange={handleTypeChange}
                    fullWidth
                    margin="normal"
                    sx={{ mb: 2, mt: 2 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Account Type" variant="outlined" />
                    )}
                />

                <Autocomplete
                    options={currencies}
                    getOptionLabel={(option) => option.label}
                    value={currencies.find((option) => option.value === formData.currency) || null}
                    onChange={handleCurrencyChange}
                    fullWidth
                    margin="normal"
                    sx={{ mb: 1, mt: 3 }}
                    renderInput={(params) => (
                        <TextField {...params} label="Select Currency" variant="outlined" />
                    )}
                />

                <TextField
                    label="Report Day"
                    name="start_day"
                    onChange={handleInputChange}
                    value={formData.start_day}
                    fullWidth
                    margin="normal"
                    type="text"
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="Description"
                    name="description"
                    onChange={handleInputChange}
                    value={formData.description}
                    fullWidth
                    margin="normal"
                    type="text"
                    InputLabelProps={{ shrink: true }}
                />

                <Box display="flex" justifyContent="flex-end" mt={2}>
                    <Button disabled={!account} onClick={handleDelete} sx={{ mr: 2, backgroundColor: "red" }} variant="gradient" color="error">
                        Delete
                    </Button>
                    <Button onClick={handleClose} sx={{ mr: 2 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} variant="contained" color="info">
                        {account ? "Update" : "Create"}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default SaveModal;
